export type ActionScope = 'record' | 'table' | 'module' | 'global';

export interface ActionContext<TEnv = unknown> {
  appId: string;
  moduleId?: string;
  actor?: {
    id?: string;
    name?: string;
    email?: string;
  };
  env?: TEnv;
  log?: (event: ActionLogEvent) => void | Promise<void>;
  refresh?: (target?: ActionRefreshTarget) => void | Promise<void>;
}

export interface ActionLogEvent {
  actionId: string;
  status: ActionExecutionStatus;
  scope: ActionScope;
  targetId?: string;
  message?: string;
  meta?: Record<string, unknown>;
}

export interface ActionRefreshTarget {
  moduleId?: string;
  tableId?: string;
  recordId?: string;
}

export type ActionExecutionStatus =
  | 'idle'
  | 'confirming'
  | 'validating'
  | 'running'
  | 'success'
  | 'error'
  | 'cancelled';

export interface ActionResult<TData = unknown> {
  ok: boolean;
  message?: string;
  data?: TData;
  refresh?: ActionRefreshTarget;
}

export type ActionHandler<TInput = unknown, TData = unknown, TEnv = unknown> = (
  input: TInput,
  context: ActionContext<TEnv>,
) => Promise<ActionResult<TData>> | ActionResult<TData>;

export interface ActionDefinition<TInput = unknown, TData = unknown, TEnv = unknown> {
  id: string;
  label: string;
  description?: string;
  scope: ActionScope;
  confirm?: boolean;
  inputSchema?: unknown;
  requiredConnectors?: string[];
  handler: ActionHandler<TInput, TData, TEnv>;
}

export interface ActionExecutionSnapshot<TData = unknown> {
  actionId: string;
  status: ActionExecutionStatus;
  result?: ActionResult<TData>;
  error?: Error;
}

export function createActionDefinition<TInput = unknown, TData = unknown, TEnv = unknown>(
  definition: ActionDefinition<TInput, TData, TEnv>,
): ActionDefinition<TInput, TData, TEnv> {
  return definition;
}

export function createActionRegistry(actions: ActionDefinition[] = []) {
  const entries = new Map(actions.map((action) => [action.id, action]));

  return {
    all: () => Array.from(entries.values()),
    get: (id: string) => entries.get(id),
    register: (action: ActionDefinition) => {
      if (entries.has(action.id)) {
        throw new Error(`Action already registered: ${action.id}`);
      }
      entries.set(action.id, action);
      return action;
    },
    execute: async <TInput = unknown, TData = unknown, TEnv = unknown>(
      id: string,
      input: TInput,
      context: ActionContext<TEnv>,
    ): Promise<ActionExecutionSnapshot<TData>> => {
      const action = entries.get(id) as ActionDefinition<TInput, TData, TEnv> | undefined;
      if (!action) {
        return {
          actionId: id,
          status: 'error',
          error: new Error(`Action not found: ${id}`),
        };
      }

      await context.log?.({
        actionId: action.id,
        scope: action.scope,
        status: 'running',
      });

      try {
        const result = await action.handler(input, context);
        await context.log?.({
          actionId: action.id,
          scope: action.scope,
          status: result.ok ? 'success' : 'error',
          message: result.message,
        });
        if (result.refresh) await context.refresh?.(result.refresh);
        return { actionId: action.id, status: result.ok ? 'success' : 'error', result };
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        await context.log?.({
          actionId: action.id,
          scope: action.scope,
          status: 'error',
          message: err.message,
        });
        return { actionId: action.id, status: 'error', error: err };
      }
    },
  };
}
